import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { AppContext } from '../context/AppContext';
import { useTranslation } from 'react-i18next';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import TransferReportPrint from '../components/TransferReportPrint';
import { Check, X, Trash2, Plus, ArrowRight } from 'lucide-react';

// Floating suggestions portal to escape any table or card overflow
const ProductSuggestionsPortal = ({
    anchorEl,
    suggestions,
    isOpen,
    onSelect,
    selectedIndex,
    t
}) => {
    const [coords, setCoords] = useState(null);

    useEffect(() => {
        if (!isOpen || !anchorEl) {
            setCoords(null);
            return;
        }

        const updatePosition = () => {
            const rect = anchorEl.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const showAbove = spaceBelow < 250 && rect.top > 250;
            setCoords({
                left: Math.max(12, rect.left),
                width: Math.max(rect.width, 360),
                top: showAbove ? undefined : rect.bottom + 6,
                bottom: showAbove ? window.innerHeight - rect.top + 6 : undefined
            });
        };

        updatePosition();
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [isOpen, anchorEl]);

    if (!isOpen || !coords || !suggestions || suggestions.length === 0) return null;

    return createPortal(
        <div
            style={{
                position: 'fixed',
                left: `${coords.left}px`,
                width: `${coords.width}px`,
                top: coords.top !== undefined ? `${coords.top}px` : undefined,
                bottom: coords.bottom !== undefined ? `${coords.bottom}px` : undefined,
                zIndex: 9999
            }}
            className="product-suggestions-portal bg-white border border-gray-200 rounded-xl shadow-2xl p-2 max-h-64 overflow-y-auto space-y-1 animate-in fade-in zoom-in-95 duration-150"
            onMouseDown={e => e.preventDefault()} // Keeps focus on input
        >
            <div className="px-2.5 py-1 text-[11px] font-bold text-gray-400 uppercase tracking-wider flex justify-between border-b border-gray-100 mb-1">
                <span>{t('Matching Products')}</span>
                <span>{t('Available Stock')}</span>
            </div>
            {suggestions.map((s, idx) => {
                const isSelected = idx === selectedIndex;
                const stockQty = parseFloat(s.quantity ?? 0);
                const isOutOfStock = stockQty <= 0;
                return (
                    <button
                        key={s.id || s.product_id || idx}
                        type="button"
                        onClick={() => onSelect(s)}
                        className={`w-full text-left p-2.5 rounded-lg flex justify-between items-center transition group/item ${
                            isSelected
                                ? 'bg-orange-50 border border-orange-300 shadow-sm'
                                : 'hover:bg-orange-50/70 border border-transparent'
                        }`}
                    >
                        <div className="flex flex-col min-w-0 pr-2">
                            <span className="text-sm font-bold text-gray-800 group-hover/item:text-orange-700 truncate">
                                {s.product_name || s.name}
                            </span>
                            <div className="flex items-center gap-2 mt-0.5">
                                {(s.barcode || s.sku) && (
                                    <span className="text-[11px] font-mono text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                                        {s.barcode || s.sku}
                                    </span>
                                )}
                                {s.category && (
                                    <span className="text-[10px] text-gray-400">
                                        {s.category}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                            <span className={`text-xs font-black px-2.5 py-1 rounded-md ${
                                isOutOfStock
                                    ? 'text-red-700 bg-red-50 border border-red-200'
                                    : 'text-emerald-700 bg-emerald-50 border border-emerald-200'
                            }`}>
                                {stockQty} {t('Units')}
                            </span>
                        </div>
                    </button>
                );
            })}
        </div>,
        document.body
    );
};

const Transfers = () => {
    const { authFetch, depos, user } = useContext(AppContext);
    const { t } = useTranslation();

    const [transfers, setTransfers] = useState([]);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [selectedTransfer, setSelectedTransfer] = useState(null);
    const [editForm, setEditForm] = useState({ product_name: '', from_depo_name: '', to_depo_name: '', quantity: '', user_name: '', created_at: '' });
    const [loading, setLoading] = useState(false);
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');
    const [historyError, setHistoryError] = useState('');
    const [historySuccess, setHistorySuccess] = useState('');

    // Filtering & Printing
    const [dateFilter, setDateFilter] = useState('today');
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');
    const [depoFilter, setDepoFilter] = useState('all');
    const [isPrintOpen, setIsPrintOpen] = useState(false);

    // Transfer Ticket Form (Multi-Item)
    const [fromDepoId, setFromDepoId] = useState(user?.depo_id || '');
    const [toDepoId, setToDepoId] = useState('');
    const [rows, setRows] = useState([
        { id: Date.now(), product_id: '', product_name: '', quantity: '1', current_stock: null, suggestions: [], isMatched: false }
    ]);

    // Cached stock for currently selected source warehouse
    const [depoStock, setDepoStock] = useState([]);
    const [activeDropdown, setActiveDropdown] = useState({ rowId: null, selectedIndex: 0 });

    const inputRefs = useRef({});
    const quantityInputRefs = useRef({});
    const fallbackTimeoutRef = useRef(null);

    // Fetch stock for source depo
    const loadDepoStock = useCallback(async (depoId) => {
        if (!depoId) {
            setDepoStock([]);
            return;
        }
        try {
            const res = await authFetch(`/api/stock?depo_id=${depoId}`);
            if (res.ok) {
                const data = await res.json();
                setDepoStock(Array.isArray(data) ? data : []);
            }
        } catch (e) {
            console.error("Failed to load depo stock", e);
        }
    }, [authFetch]);

    useEffect(() => {
        if (fromDepoId) {
            loadDepoStock(fromDepoId);
        } else {
            setDepoStock([]);
        }
    }, [fromDepoId, loadDepoStock]);

    // Close dropdown on click outside
    useEffect(() => {
        const handleDocClick = (e) => {
            if (!e.target.closest('.product-search-input') && !e.target.closest('.product-suggestions-portal')) {
                setActiveDropdown({ rowId: null, selectedIndex: 0 });
            }
        };
        document.addEventListener('mousedown', handleDocClick);
        return () => document.removeEventListener('mousedown', handleDocClick);
    }, []);

    const fetchTransfers = () => {
        setLoading(true);
        setHistoryError('');
        authFetch('/api/transfers')
            .then(res => {
                if (!res.ok) throw new Error('Bad response');
                return res.json();
            })
            .then(data => setTransfers(Array.isArray(data) ? data : []))
            .catch(() => setHistoryError(t('Failed to load transfers')))
            .finally(() => setLoading(false));
    };

    useEffect(() => { fetchTransfers(); }, []);

    const handleFromDepoChange = (newDepoId) => {
        if (rows.some(r => r.product_id !== '')) {
            if (window.confirm(t("Changing the source warehouse will clear your current selection. Proceed?"))) {
                setFromDepoId(newDepoId);
                setRows([{ id: Date.now(), product_id: '', product_name: '', quantity: '1', current_stock: null, suggestions: [], isMatched: false }]);
            }
        } else {
            setFromDepoId(newDepoId);
            setRows([{ id: Date.now(), product_id: '', product_name: '', quantity: '1', current_stock: null, suggestions: [], isMatched: false }]);
        }
    };

    const addRow = () => {
        const newId = Date.now();
        setRows(prev => [
            ...prev,
            { id: newId, product_id: '', product_name: '', quantity: '1', current_stock: null, suggestions: [], isMatched: false }
        ]);
        setTimeout(() => {
            inputRefs.current[newId]?.focus();
        }, 50);
    };

    const removeRow = (id) => {
        if (rows.length > 1) {
            setRows(prev => prev.filter(r => r.id !== id));
        } else {
            setRows([{ id: Date.now(), product_id: '', product_name: '', quantity: '1', current_stock: null, suggestions: [], isMatched: false }]);
        }
    };

    // Debounced catalog search fallback for products not yet stocked in this depo
    const triggerCatalogFallback = (rowId, query) => {
        if (fallbackTimeoutRef.current) clearTimeout(fallbackTimeoutRef.current);
        fallbackTimeoutRef.current = setTimeout(async () => {
            try {
                const res = await authFetch(`/api/products?search=${encodeURIComponent(query)}`);
                if (res.ok) {
                    const catalogItems = await res.json();
                    if (Array.isArray(catalogItems) && catalogItems.length > 0) {
                        setRows(prevRows => prevRows.map(r => {
                            if (r.id !== rowId || r.isMatched) return r;
                            const existingIds = new Set((r.suggestions || []).map(s => String(s.product_id || s.id)));
                            const newSuggestions = [...(r.suggestions || [])];
                            for (const item of catalogItems) {
                                if (!existingIds.has(String(item.id))) {
                                    newSuggestions.push({
                                        product_id: item.id,
                                        product_name: item.name,
                                        barcode: item.barcode,
                                        sku: item.sku,
                                        category: item.category,
                                        quantity: 0
                                    });
                                }
                            }
                            return { ...r, suggestions: newSuggestions.slice(0, 10) };
                        }));
                    }
                }
            } catch (e) {
                console.error("Catalog fallback error", e);
            }
        }, 300);
    };

    // Instant local typing & search without race conditions or cuts
    const handleProductChange = (rowId, value) => {
        setRows(prevRows => prevRows.map(r => {
            if (r.id !== rowId) return r;

            const newRow = { ...r, product_name: value };

            if (r.isMatched && value !== r.product_name) {
                newRow.isMatched = false;
                newRow.product_id = '';
                newRow.current_stock = null;
            }

            const query = value.trim().toLowerCase();
            let suggestions = [];
            if (query.length > 0) {
                suggestions = depoStock.filter(p => {
                    const name = (p.product_name || p.name || '').toLowerCase();
                    const barcode = (p.barcode || '').toLowerCase();
                    const sku = (p.sku || '').toLowerCase();
                    return name.includes(query) || barcode.includes(query) || sku.includes(query);
                }).slice(0, 10);

                // Exact match check on barcode, SKU, or full name
                const exact = depoStock.find(p => {
                    const barcode = (p.barcode || '').trim().toLowerCase();
                    const sku = (p.sku || '').trim().toLowerCase();
                    const name = (p.product_name || p.name || '').trim().toLowerCase();
                    return (barcode && barcode === query) || (sku && sku === query) || (name && name === query);
                });

                if (exact) {
                    newRow.product_id = exact.product_id || exact.id;
                    newRow.product_name = exact.product_name || exact.name;
                    newRow.current_stock = exact.quantity;
                    newRow.isMatched = true;
                    if (!newRow.quantity || parseFloat(newRow.quantity) <= 0) {
                        newRow.quantity = '1';
                    }
                    suggestions = [];
                }
            }

            newRow.suggestions = suggestions;
            return newRow;
        }));

        setActiveDropdown({ rowId, selectedIndex: 0 });

        if (value.trim().length >= 2) {
            triggerCatalogFallback(rowId, value.trim());
        }
    };

    const selectProduct = (rowId, prod) => {
        setRows(prevRows => prevRows.map(r => {
            if (r.id === rowId) {
                return {
                    ...r,
                    product_id: prod.product_id || prod.id,
                    product_name: prod.product_name || prod.name,
                    current_stock: prod.quantity,
                    isMatched: true,
                    suggestions: [],
                    quantity: (!r.quantity || parseFloat(r.quantity) <= 0) ? '1' : r.quantity
                };
            }
            return r;
        }));
        setActiveDropdown({ rowId: null, selectedIndex: 0 });

        setTimeout(() => {
            if (quantityInputRefs.current[rowId]) {
                quantityInputRefs.current[rowId].focus();
                quantityInputRefs.current[rowId].select();
            }
        }, 50);
    };

    const handleClearProduct = (rowId) => {
        setRows(prevRows => prevRows.map(r => {
            if (r.id === rowId) {
                return {
                    ...r,
                    product_id: '',
                    product_name: '',
                    current_stock: null,
                    isMatched: false,
                    suggestions: []
                };
            }
            return r;
        }));
        setActiveDropdown({ rowId: null, selectedIndex: 0 });
        inputRefs.current[rowId]?.focus();
    };

    const handleProductKeyDown = (rowId, e) => {
        const row = rows.find(r => r.id === rowId);
        const suggestions = row?.suggestions || [];

        if (activeDropdown.rowId === rowId && suggestions.length > 0) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setActiveDropdown(prev => ({
                    ...prev,
                    selectedIndex: (prev.selectedIndex + 1) % suggestions.length
                }));
                return;
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActiveDropdown(prev => ({
                    ...prev,
                    selectedIndex: (prev.selectedIndex - 1 + suggestions.length) % suggestions.length
                }));
                return;
            }
            if (e.key === 'Enter') {
                e.preventDefault();
                const target = suggestions[activeDropdown.selectedIndex] || suggestions[0];
                if (target) {
                    selectProduct(rowId, target);
                }
                return;
            }
            if (e.key === 'Escape') {
                e.preventDefault();
                setActiveDropdown({ rowId: null, selectedIndex: 0 });
                return;
            }
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const query = (row?.product_name || '').trim().toLowerCase();
            if (query) {
                const match = depoStock.find(p => {
                    const barcode = (p.barcode || '').trim().toLowerCase();
                    const sku = (p.sku || '').trim().toLowerCase();
                    const name = (p.product_name || p.name || '').trim().toLowerCase();
                    return barcode === query || sku === query || name === query || name.includes(query);
                });
                if (match) {
                    selectProduct(rowId, match);
                }
            }
        }
    };

    const handleProductBlur = (rowId) => {
        setTimeout(() => {
            setRows(prevRows => prevRows.map(r => {
                if (r.id !== rowId || r.isMatched) return r;
                const query = (r.product_name || '').trim().toLowerCase();
                if (!query) return r;
                const match = depoStock.find(p => {
                    const barcode = (p.barcode || '').trim().toLowerCase();
                    const sku = (p.sku || '').trim().toLowerCase();
                    const name = (p.product_name || p.name || '').trim().toLowerCase();
                    return barcode === query || sku === query || name === query;
                });
                if (match) {
                    return {
                        ...r,
                        product_id: match.product_id || match.id,
                        product_name: match.product_name || match.name,
                        current_stock: match.quantity,
                        isMatched: true,
                        suggestions: [],
                        quantity: (!r.quantity || parseFloat(r.quantity) <= 0) ? '1' : r.quantity
                    };
                }
                return r;
            }));
        }, 200);
    };

    const updateRow = (id, field, value) => {
        setRows(prevRows => prevRows.map(r => {
            if (r.id === id) {
                return { ...r, [field]: value };
            }
            return r;
        }));
    };

    const isOverStock = (row) => {
        if (!row.product_id || row.current_stock === null || row.current_stock === undefined) return false;
        const qty = parseFloat(row.quantity);
        const stock = parseFloat(row.current_stock);
        if (isNaN(qty) || isNaN(stock)) return false;
        return qty > stock;
    };

    const handleTransfer = async (e) => {
        e.preventDefault();
        setFormError('');
        setFormSuccess('');

        // Validation
        if (!fromDepoId || !toDepoId) return setFormError(t("Please select both source and destination."));
        if (String(fromDepoId) === String(toDepoId)) return setFormError(t("Source and destination cannot be the same."));

        // Auto-match any typed product that wasn't clicked from dropdown
        const resolvedRows = rows.map(r => {
            if (!r.isMatched && r.product_name.trim()) {
                const query = r.product_name.trim().toLowerCase();
                const match = depoStock.find(p => {
                    const barcode = (p.barcode || '').trim().toLowerCase();
                    const sku = (p.sku || '').trim().toLowerCase();
                    const name = (p.product_name || p.name || '').trim().toLowerCase();
                    return barcode === query || sku === query || name === query || name.includes(query);
                });
                if (match) {
                    return {
                        ...r,
                        product_id: match.product_id || match.id,
                        product_name: match.product_name || match.name,
                        current_stock: match.quantity,
                        isMatched: true
                    };
                }
            }
            return r;
        });

        const itemsToTransfer = resolvedRows.filter(r => r.product_id && parseFloat(r.quantity) > 0);
        if (itemsToTransfer.length === 0) return setFormError(t("Please add at least one valid item with quantity greater than 0."));

        // Check stock availability (numerical)
        for (const item of itemsToTransfer) {
            const qty = parseFloat(item.quantity);
            const stock = parseFloat(item.current_stock ?? 0);
            if (qty > stock) {
                return setFormError(`${t('Insufficient stock for')} ${item.product_name}. (${t('Available')}: ${stock}, ${t('Requested')}: ${qty})`);
            }
        }

        setLoading(true);
        try {
            const res = await authFetch('/api/transfers', {
                method: 'POST',
                body: JSON.stringify({
                    from_depo_id: fromDepoId,
                    to_depo_id: toDepoId,
                    items: itemsToTransfer.map(item => ({
                        product_id: item.product_id,
                        quantity: parseFloat(item.quantity)
                    }))
                })
            });

            if (res.ok) {
                setFormSuccess(t("Transfer completed successfully."));
                setRows([{ id: Date.now(), product_id: '', product_name: '', quantity: '1', current_stock: null, suggestions: [], isMatched: false }]);
                fetchTransfers();
                if (fromDepoId) loadDepoStock(fromDepoId);
            } else {
                let msg = t('Transfer failed.');
                try { const d = await res.json(); msg = d.message || msg; } catch {}
                setFormError(msg);
            }
        } catch (err) {
            setFormError(t("Network error. Please check your connection."));
        } finally {
            setLoading(false);
        }
    };

    // Filter Logic
    const getFilteredTransfers = () => {
        if (!transfers) return [];
        const now = new Date();
        const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const startOfWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay());
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

        return transfers.filter(t => {
            const transferDate = new Date(t.created_at);

            let dateMatch = true;
            if (dateFilter === 'today') dateMatch = transferDate >= startOfDay;
            else if (dateFilter === 'this_week') dateMatch = transferDate >= startOfWeek;
            else if (dateFilter === 'this_month') dateMatch = transferDate >= startOfMonth;
            else if (dateFilter === 'custom' && customStartDate && customEndDate) {
                const start = new Date(customStartDate);
                start.setHours(0, 0, 0, 0);
                const end = new Date(customEndDate);
                end.setHours(23, 59, 59, 999);
                dateMatch = transferDate >= start && transferDate <= end;
            }

            let depoMatch = true;
            if (depoFilter !== 'all') {
                depoMatch = (String(t.from_depo_id) === String(depoFilter) || String(t.to_depo_id) === String(depoFilter));
            }

            return dateMatch && depoMatch;
        });
    };

    const filteredTransfers = getFilteredTransfers();

    const getDateRangeText = () => {
        let text = '';
        if (dateFilter === 'today') text = t('Today') + ` (${new Date().toLocaleDateString()})`;
        else if (dateFilter === 'this_week') text = t('This Week');
        else if (dateFilter === 'this_month') text = t('This Month');
        else if (dateFilter === 'all') text = t('All Time');
        else if (dateFilter === 'custom' && customStartDate && customEndDate) text = `${customStartDate} to ${customEndDate}`;
        else text = t('Custom Range');

        if (depoFilter !== 'all') {
            const depoName = depos.find(d => String(d.id) === String(depoFilter))?.name;
            if (depoName) text += ` | Filter: ${depoName}`;
        }

        return text;
    };

    const columns = [
        { header: t('ID'), accessor: 'id' },
        { header: t('Product'), accessor: 'product_name', render: row => <span className="font-bold">{row.product_name}</span> },
        { header: t('Source (From)'), accessor: 'from_depo_name', render: row => <span className="text-orange-600 font-semibold">{row.from_depo_name}</span> },
        { header: t('Destination (To)'), accessor: 'to_depo_name', render: row => <span className="text-emerald-600 font-semibold">{row.to_depo_name}</span> },
        { header: t('Quantity'), accessor: 'quantity', render: row => <span className="font-black text-gray-700">{row.quantity}</span> },
        { header: t('Initiated By'), accessor: 'user_name' },
        { header: t('Date'), accessor: 'created_at', render: row => new Date(row.created_at).toLocaleString() },
        {
            header: t('Status'), accessor: 'status',
            render: row => {
                let badgeClass = 'bg-gray-100 text-gray-600';
                if (row.status === 'Sent') badgeClass = 'bg-yellow-100 text-yellow-700 font-bold';
                if (row.status === 'Received') badgeClass = 'bg-emerald-100 text-emerald-700 font-bold shadow-sm';
                if (row.status === 'Cancelled') badgeClass = 'bg-red-100 text-red-700 font-bold';
                return <span className={`px-2 py-1 rounded-full text-xs uppercase tracking-wider ${badgeClass}`}>{t(row.status || 'Sent')}</span>;
            }
        },
        {
            header: 'Actions',
            accessor: 'actions',
            render: (row) => (
                <div className="flex gap-2 items-center">
                    <button className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200" onClick={() => {
                        setSelectedTransfer(row);
                        setEditForm({ product_name: row.product_name, from_depo_name: row.from_depo_name, to_depo_name: row.to_depo_name, quantity: row.quantity, user_name: row.user_name, created_at: row.created_at });
                        setIsViewModalOpen(true);
                    }}>{t('View')}</button>

                    {(row.status === 'Sent' || !row.status) && (user?.role === 'Admin' || String(user?.depo_id) === String(row.to_depo_id)) && (
                        <button className="px-3 py-1 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 font-black shadow-sm" onClick={async () => {
                            if (!window.confirm(t('Mark as Received? This adds the physical stock to your warehouse.'))) return;
                            setLoading(true);
                            try {
                                const res = await authFetch(`/api/transfers?id=${row.id}&action=receive`, { method: 'PUT' });
                                if (res.ok) {
                                    fetchTransfers();
                                } else {
                                    let msg = t('Failed to receive');
                                    try { const d = await res.json(); msg = d.message || msg; } catch {}
                                    alert(msg);
                                }
                            } catch { alert(t('Network error. Please try again.')); }
                            finally { setLoading(false); }
                        }}>{t('Receive')}</button>
                    )}

                    {(row.status === 'Sent' || !row.status) && (user?.role === 'Admin' || String(user?.depo_id) === String(row.from_depo_id)) && (
                        <button className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 font-bold" onClick={async () => {
                            if (!window.confirm(t('Cancel transit and return stock to your warehouse?'))) return;
                            setLoading(true);
                            try {
                                const res = await authFetch(`/api/transfers?id=${row.id}&action=cancel`, { method: 'PUT' });
                                if (res.ok) {
                                    fetchTransfers();
                                } else {
                                    let msg = t('Failed to cancel');
                                    try { const d = await res.json(); msg = d.message || msg; } catch {}
                                    alert(msg);
                                }
                            } catch { alert(t('Network error. Please try again.')); }
                            finally { setLoading(false); }
                        }}>{t('Cancel')}</button>
                    )}
                </div>
            )
        },
    ];

    const activeRow = rows.find(r => r.id === activeDropdown.rowId);

    return (
        <div className="relative">
            <div className={isPrintOpen ? 'hidden' : ''}>
                <h2 className="text-3xl font-bold text-gray-800 mb-6">{t('Stock Transfers')}</h2>

                {/* Transfer Initiator UI */}
                <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-150 mb-10 overflow-visible relative">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-400 to-rose-500 rounded-t-xl"></div>
                    <h3 className="text-2xl font-bold text-gray-800 mb-2">{t('Initiate Transfer')}</h3>
                    <p className="text-gray-500 mb-6">{t('Move stock securely between depos. Transfers are atomic and guaranteed.')}</p>

                    {formError && <div className="bg-red-50 text-red-700 p-4 rounded-md mb-6 font-medium">{formError}</div>}
                    {formSuccess && <div className="bg-emerald-50 text-emerald-700 p-4 rounded-md mb-6 font-medium">{formSuccess}</div>}

                    <form onSubmit={handleTransfer} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
                            {/* FROM */}
                            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 relative">
                                <span className="absolute -top-3 left-4 bg-white px-2 text-sm font-bold text-orange-600">{t('Source (From)')}</span>
                                <select
                                    required
                                    disabled={user?.role !== 'Admin'}
                                    className="block w-full rounded-md border-gray-300 shadow-sm p-3 border focus:border-orange-500 focus:ring-orange-500 disabled:bg-gray-100 disabled:text-gray-500"
                                    value={fromDepoId}
                                    onChange={e => handleFromDepoChange(e.target.value)}
                                >
                                    <option value="">{t('-- Choose Source --')}</option>
                                    {depos.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                </select>
                                {user?.role !== 'Admin' && <p className="text-xs text-gray-400 mt-1">{t('Locked to your assigned Depo.')}</p>}
                            </div>

                            {/* TO */}
                            <div className="bg-emerald-50 bg-opacity-30 p-6 rounded-lg border border-emerald-100 relative">
                                <span className="absolute -top-3 left-4 bg-white px-2 text-sm font-bold text-emerald-600">{t('Destination (To)')}</span>
                                <select
                                    required
                                    className="block w-full rounded-md border-gray-300 shadow-sm p-3 border focus:border-emerald-500 focus:ring-emerald-500"
                                    value={toDepoId}
                                    onChange={e => setToDepoId(e.target.value)}
                                >
                                    <option value="">{t('-- Choose Destination --')}</option>
                                    {depos.filter(d => String(d.id) !== String(fromDepoId)).map(d => (
                                        <option key={d.id} value={d.id}>{d.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* MULTI-ITEM TABLE */}
                        <div className="bg-white rounded-xl border border-gray-200 shadow-sm min-h-[140px] overflow-visible">
                            <table className="w-full text-left border-collapse">
                                <thead className="bg-gray-50/90 text-gray-500 text-xs uppercase font-bold border-b border-gray-200">
                                    <tr>
                                        <th className="p-4 w-[48%]">{t('Product')}</th>
                                        <th className="p-4 w-[22%]">{t('Available Stock')}</th>
                                        <th className="p-4 w-[24%]">{t('Quantity to Transfer')}</th>
                                        <th className="p-4 w-[6%] text-center"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {rows.map((row) => {
                                        const overStock = isOverStock(row);
                                        return (
                                            <tr key={row.id} className="hover:bg-orange-50/20 transition group">
                                                {/* PRODUCT INPUT */}
                                                <td className="p-4">
                                                    <div className="relative product-search-input">
                                                        <div className="relative flex items-center">
                                                            <input
                                                                ref={el => inputRefs.current[row.id] = el}
                                                                required
                                                                autoComplete="off"
                                                                disabled={!fromDepoId}
                                                                value={row.product_name}
                                                                onChange={e => handleProductChange(row.id, e.target.value)}
                                                                onFocus={() => {
                                                                    if (row.suggestions?.length > 0) {
                                                                        setActiveDropdown({ rowId: row.id, selectedIndex: 0 });
                                                                    }
                                                                }}
                                                                onKeyDown={e => handleProductKeyDown(row.id, e)}
                                                                onBlur={() => handleProductBlur(row.id)}
                                                                placeholder={fromDepoId ? t('Type full product name, barcode, or SKU...') : t('Select Source Depo first')}
                                                                className={`w-full rounded-lg py-2.5 pl-3.5 pr-8 text-sm outline-none transition border shadow-sm ${
                                                                    row.isMatched
                                                                        ? 'bg-emerald-50/50 border-emerald-300 text-emerald-900 font-bold focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100'
                                                                        : 'border-gray-200 text-gray-900 bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-100'
                                                                } disabled:bg-gray-100 disabled:text-gray-400`}
                                                            />
                                                            {row.isMatched ? (
                                                                <span className="absolute right-3 text-emerald-600 text-sm font-bold flex items-center gap-1" title={t('Matched')}>
                                                                    <Check className="w-4 h-4 text-emerald-600 stroke-[3]" />
                                                                </span>
                                                            ) : row.product_name ? (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleClearProduct(row.id)}
                                                                    className="absolute right-3 text-gray-300 hover:text-gray-600 transition"
                                                                >
                                                                    <X className="w-4 h-4" />
                                                                </button>
                                                            ) : null}
                                                        </div>
                                                        {row.isMatched && row.current_stock !== null && (
                                                            <div className="flex items-center gap-2 mt-1 px-1">
                                                                <span className="text-[11px] font-semibold text-emerald-700">
                                                                    ✓ {t('Product linked')}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>

                                                {/* AVAILABLE STOCK */}
                                                <td className="p-4">
                                                    {row.isMatched && row.current_stock !== null ? (
                                                        <div className="flex items-baseline gap-1.5">
                                                            <span className={`text-base font-black ${
                                                                parseFloat(row.current_stock) <= 0 ? 'text-red-500' : 'text-blue-600'
                                                            }`}>
                                                                {row.current_stock}
                                                            </span>
                                                            <span className="text-xs text-gray-400 font-semibold">{t('Units')}</span>
                                                        </div>
                                                    ) : (
                                                        <span className="text-gray-300 font-bold text-lg select-none">—</span>
                                                    )}
                                                </td>

                                                {/* QUANTITY TO TRANSFER */}
                                                <td className="p-4">
                                                    <div className="relative">
                                                        <div className={`flex items-center rounded-lg border shadow-sm transition bg-white overflow-hidden max-w-[170px] ${
                                                            overStock
                                                                ? 'border-red-300 ring-2 ring-red-100'
                                                                : 'border-gray-200 focus-within:border-emerald-500 focus-within:ring-2 focus-within:ring-emerald-100'
                                                        }`}>
                                                            <button
                                                                type="button"
                                                                disabled={parseFloat(row.quantity) <= 1}
                                                                onClick={() => {
                                                                    const current = parseFloat(row.quantity) || 1;
                                                                    if (current > 1) {
                                                                        updateRow(row.id, 'quantity', String(Math.max(1, current - 1)));
                                                                    }
                                                                }}
                                                                className="px-2.5 py-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent transition text-sm font-bold select-none"
                                                            >
                                                                −
                                                            </button>
                                                            <input
                                                                type="number"
                                                                step="any"
                                                                min="0.01"
                                                                required
                                                                ref={el => quantityInputRefs.current[row.id] = el}
                                                                className={`w-full text-center p-2 text-sm font-bold outline-none border-none bg-transparent ${
                                                                    overStock ? 'text-red-700 bg-red-50/50' : 'text-gray-900'
                                                                }`}
                                                                value={row.quantity}
                                                                onChange={e => updateRow(row.id, 'quantity', e.target.value)}
                                                                placeholder="1"
                                                                onKeyDown={e => {
                                                                    if (e.key === 'Enter') {
                                                                        e.preventDefault();
                                                                        const isLast = rows[rows.length - 1].id === row.id;
                                                                        if (isLast && row.product_id) {
                                                                            addRow();
                                                                        }
                                                                    }
                                                                }}
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    const current = parseFloat(row.quantity) || 0;
                                                                    const maxStock = row.current_stock !== null ? parseFloat(row.current_stock) : Infinity;
                                                                    const next = current + 1;
                                                                    if (next <= maxStock) {
                                                                        updateRow(row.id, 'quantity', String(next));
                                                                    }
                                                                }}
                                                                className="px-2.5 py-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition text-sm font-bold select-none"
                                                            >
                                                                +
                                                            </button>
                                                        </div>
                                                        {overStock && (
                                                            <p className="absolute -bottom-5 left-0 text-[10px] text-red-500 font-bold whitespace-nowrap">
                                                                {t('Exceeds available stock!')} ({row.current_stock})
                                                            </p>
                                                        )}
                                                    </div>
                                                </td>

                                                {/* DELETE ROW */}
                                                <td className="p-4 text-center">
                                                    <button
                                                        type="button"
                                                        onClick={() => removeRow(row.id)}
                                                        className="text-gray-300 hover:text-red-500 hover:bg-red-50 p-2 rounded-lg transition"
                                                        title={t('Remove Row')}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Floating Suggestions Portal */}
                        {activeDropdown.rowId && (
                            <ProductSuggestionsPortal
                                anchorEl={inputRefs.current[activeDropdown.rowId]}
                                suggestions={activeRow?.suggestions || []}
                                isOpen={Boolean(
                                    activeDropdown.rowId &&
                                    !activeRow?.isMatched &&
                                    (activeRow?.suggestions?.length || 0) > 0
                                )}
                                selectedIndex={activeDropdown.selectedIndex}
                                onSelect={(prod) => selectProduct(activeDropdown.rowId, prod)}
                                t={t}
                            />
                        )}

                        <div className="flex justify-between items-center pt-4 border-t border-gray-100">
                            <button
                                type="button"
                                onClick={addRow}
                                className="group flex items-center gap-2 text-orange-600 font-bold uppercase text-xs hover:text-orange-800 transition py-2 px-3 rounded-lg hover:bg-orange-50"
                            >
                                <span className="w-7 h-7 rounded-full bg-orange-100 flex items-center justify-center group-hover:bg-orange-600 group-hover:text-white transition font-black text-sm">
                                    +
                                </span>
                                {t('Add Another Item')}
                            </button>

                            <button
                                type="submit"
                                disabled={loading || !fromDepoId || !toDepoId || !rows.some(r => (r.product_id || r.product_name.trim()) && parseFloat(r.quantity) > 0) || rows.some(r => isOverStock(r))}
                                className="bg-gray-900 text-white px-8 py-3 rounded-lg shadow-md hover:bg-black transition font-bold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {loading ? (
                                    <>
                                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                                        {t('Processing...')}
                                    </>
                                ) : (
                                    <>
                                        {t('Confirm Batch Transfer')}
                                        <ArrowRight className="w-4 h-4" />
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>

                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-2xl font-bold text-gray-800">{t('Transfer History')}</h3>
                    <button
                        onClick={() => setIsPrintOpen(true)}
                        disabled={filteredTransfers.length === 0}
                        className="bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300 font-bold px-4 py-2 rounded-xl transition shadow-sm flex items-center gap-2 disabled:opacity-50"
                    >
                        🖨️ {t('Print Report')}
                    </button>
                </div>

                {historyError && <div className="bg-red-100 text-red-700 p-2 rounded mb-2">{historyError}</div>}
                {historySuccess && <div className="bg-emerald-100 text-emerald-700 p-2 rounded mb-2">{historySuccess}</div>}

                {/* Date Filters */}
                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 mb-6 flex flex-wrap gap-4 items-center">
                    <div className="flex gap-2">
                        {['today', 'this_week', 'this_month', 'all'].map(filter => (
                            <button
                                key={filter}
                                onClick={() => setDateFilter(filter)}
                                className={`px-4 py-2 rounded-lg text-sm font-bold uppercase tracking-wide transition ${dateFilter === filter
                                    ? 'bg-orange-100 text-orange-700 border-2 border-orange-500 shadow-inner'
                                    : 'bg-gray-50 text-gray-600 border border-transparent hover:bg-gray-100'
                                    }`}
                            >
                                {t(filter.replace('_', ' '))}
                            </button>
                        ))}
                        <button
                            onClick={() => setDateFilter('custom')}
                            className={`px-4 py-2 rounded-lg text-sm font-bold uppercase tracking-wide transition ${dateFilter === 'custom' ? 'bg-orange-100 text-orange-700 border-2 border-orange-500 shadow-inner' : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-transparent'
                                }`}
                        >
                            {t('Custom')}
                        </button>
                    </div>

                    {dateFilter === 'custom' && (
                        <div className="flex items-center gap-2 border-l pl-4 border-gray-200 ml-2">
                            <input type="date" value={customStartDate} onChange={e => setCustomStartDate(e.target.value)} className="border p-2 rounded text-sm text-gray-600" />
                            <span className="text-gray-400 font-medium">to</span>
                            <input type="date" value={customEndDate} onChange={e => setCustomEndDate(e.target.value)} className="border p-2 rounded text-sm text-gray-600" />
                        </div>
                    )}

                    <div className="flex items-center gap-4 border-l pl-4 border-gray-200 ml-2">
                        <span className="text-sm font-bold text-gray-500 uppercase">{t('Branch')}:</span>
                        <select
                            value={depoFilter}
                            onChange={e => setDepoFilter(e.target.value)}
                            className="border-gray-300 rounded-lg text-sm font-bold text-gray-700 p-2 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                        >
                            <option value="all">{t('All Branches')}</option>
                            {depos.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                        </select>
                    </div>

                    <div className="ml-auto text-sm font-medium text-gray-500 flex items-center gap-2">
                        {t('Showing:')} <span className="bg-gray-100 px-2 py-1 rounded text-gray-800 font-bold">{filteredTransfers.length}</span> {t('records')}
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <DataTable columns={columns} data={filteredTransfers} />
                </div>

                {/* View/Edit Modal */}
                <Modal isOpen={isViewModalOpen} onClose={() => setIsViewModalOpen(false)} title={selectedTransfer ? `${t('Transfer #')}${selectedTransfer.id}` : t('Transfer Details')}>
                    {selectedTransfer && (
                        <div className="space-y-4">
                            <form onSubmit={async (e) => {
                                e.preventDefault();
                                setLoading(true);
                                setHistoryError('');
                                setHistorySuccess('');
                                try {
                                    const res = await authFetch(`/api/transfers/${selectedTransfer.id}`, { method: 'PUT', body: JSON.stringify(editForm) });
                                    if (!res.ok) throw new Error(t('Failed to update transfer'));
                                    setHistorySuccess(t('Transfer updated successfully'));
                                    setIsViewModalOpen(false);
                                    setSelectedTransfer(null);
                                    fetchTransfers();
                                } catch (err) {
                                    setHistoryError(t('Failed to update transfer'));
                                } finally {
                                    setLoading(false);
                                }
                            }} className="space-y-4">
                                <div><label className="block text-sm font-medium">{t('Product')}</label><input required className="w-full border p-2 rounded mt-1" value={editForm.product_name} onChange={e => setEditForm({ ...editForm, product_name: e.target.value })} /></div>
                                <div><label className="block text-sm font-medium">{t('Source (From)')}</label><input required className="w-full border p-2 rounded mt-1" value={editForm.from_depo_name} onChange={e => setEditForm({ ...editForm, from_depo_name: e.target.value })} /></div>
                                <div><label className="block text-sm font-medium">{t('Destination (To)')}</label><input required className="w-full border p-2 rounded mt-1" value={editForm.to_depo_name} onChange={e => setEditForm({ ...editForm, to_depo_name: e.target.value })} /></div>
                                <div><label className="block text-sm font-medium">{t('Quantity')}</label><input required type="number" className="w-full border p-2 rounded mt-1" value={editForm.quantity} onChange={e => setEditForm({ ...editForm, quantity: e.target.value })} /></div>
                                <div><label className="block text-sm font-medium">{t('Initiated By')}</label><input required className="w-full border p-2 rounded mt-1" value={editForm.user_name} onChange={e => setEditForm({ ...editForm, user_name: e.target.value })} /></div>
                                <div><label className="block text-sm font-medium">{t('Date')}</label><input required type="date" className="w-full border p-2 rounded mt-1" value={editForm.created_at ? new Date(editForm.created_at).toISOString().split('T')[0] : ''} onChange={e => setEditForm({ ...editForm, created_at: e.target.value })} /></div>
                                <div className="flex gap-2 pt-2">
                                    <button type="submit" className="bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-700 transition">{t('Save Changes')}</button>
                                    <button type="button" className="bg-gray-200 px-4 py-2 rounded hover:bg-gray-300 transition" onClick={() => setIsViewModalOpen(false)}>{t('Cancel')}</button>
                                </div>
                            </form>
                        </div>
                    )}
                </Modal>
            </div>

            {/* Print Overlay */}
            {isPrintOpen && (
                <TransferReportPrint
                    transfers={filteredTransfers}
                    dateRangeText={getDateRangeText()}
                    onClose={() => setIsPrintOpen(false)}
                />
            )}
        </div>
    );
};

export default Transfers;
